import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isValidEmail } from '../../../utils/utils';
import { confirmCode, setOnboardingRoute, setTempNumber } from '../../../stores/actions/session';
import TextInput from '../../general-components/text-input';
import { APP, ONBOARDING_ROUTES } from '../../../stores/session-status';
import TellyChannelLogo from '../../../images/app-store-icon.png';
import { GreenroomState as GS } from '../../../stores/types/types';
import * as SessionActions from '../../../stores/actions/session';
import { getStorageItem } from '../../../utils/local-storage';
import { StoredTokens } from '../../../stores/types/session';

// NOTE: This component is used for the "producer enter email" screen prior
// to selecting an app AND after they select an app
// this is the sign in component as well with the login form.

/* eslint-disable no-nested-ternary */
// according to eslint, the only reasoning for this is...
// "Nesting ternary expressions can make code more difficult to understand."
// you may try to refactor this if you want, but it's not a priority.

export default function SignInProducer() {
	const invitationError = useSelector((s: GS) => s.SessionReducer.invitationError);
	const user = useSelector((s: GS) => s.SessionReducer.user);
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const shouldRefresh = useSelector((s: GS) => s.SessionReducer.shouldRefresh);
	const onboardingRoute = useSelector((s: GS) => s.SessionReducer.onboarding_route);
	const emailEntered = useSelector((s: GS) => s.SessionReducer.emailEntered);
	const signInError = useSelector((s: GS) => s.SessionReducer.signInError);
	const selectedApp = useSelector((s: GS) => s.SessionReducer.selectedApp);
	const verifying = useSelector((s: GS) => s.SessionReducer.verifying);
	const requestPhone = useSelector((s: GS) => s.SessionReducer.request_phone);

	const isEventsProducer = useSelector((s: GS) => s.SessionReducer.isEventsProducer);
	const isEventsLegacyProducer = useSelector((s: GS) => s.SessionReducer.isEventsLegacyProducer);
	const isStandaloneProducer = useSelector((s: GS) => s.SessionReducer.isStandaloneProducer);
	// Events two factor auth
	const phoneHash = useSelector((s: GS) => s.SessionReducer.phone_hash);
	const tempProfile = useSelector((s: GS) => s.SessionReducer.temp_profile);
	const twoFactorStored = useSelector((s: GS) => s.SessionReducer.two_factor_stored);
	const dispatch = useDispatch();

	const [email, setEmail] = useState<string>('');
	const [password, setPassword] = useState<string>('');
	const [emailValid, setEmailValid] = useState(false);
	// Events two factor auth
	const [twoFactorAuthFormVisible, setTwoFactorAuthFormVisible] = useState(false);
	const [code, setCode] = useState('');
	const [phoneNumber, setPhoneNumber] = useState<string>('');

	useEffect(() => {
		// Remove error any time this screen loads (ex: Error visible, producer
		// navigates back to "select app", then comes back to this screen)
		dispatch(SessionActions.setSignInError(''));
	}, []);

	useEffect(() => {
		if (token) {
			// Anytime a producer signs in we refresh the page. This refresh logic was
			// originally created because it was simpler
			// to refresh the page when a producer successfully signed in for
			// a meeting than it was to modify the state. Next page state
			// is handled in session-status.ts on refresh
			if (shouldRefresh) {
				window.location.reload();
			}
		}
	}, [token, setOnboardingRoute, shouldRefresh, user]);

	useEffect(() => {
		if (emailEntered) {
			// Producer already entered email on "enter email" first screen
			setEmail(emailEntered);
			setEmailValid(true);
		}
	}, []);

	useEffect(() => {
		if (!signInError && tempProfile) setTwoFactorAuthFormVisible(true);
	}, [signInError, tempProfile]);

	const isSingleAppProducer = useMemo((): boolean => {
		const producerAppCount: number = [
			isEventsProducer,
			isEventsLegacyProducer,
			isStandaloneProducer,
		].reduce((acc: number, cur?: boolean) => {
			if (cur) {
				return acc + 1;
			}

			return acc;
		}, 0);

		return producerAppCount < 2;
	}, [isEventsLegacyProducer, isEventsProducer, isStandaloneProducer]);

	// Events two factor auth
	function handleSetPhoneNumber(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();

		if (tempProfile) {
			dispatch(setTempNumber(phoneNumber, tempProfile));
		}
	}

	// Events two factor auth
	function handleAuthCodeSubmit() {
		// send confirmation code if user has tempProfile key
		if (tempProfile) {
			dispatch(confirmCode(tempProfile, code, true));
		}
	}

	async function submitForm(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		// Events two factor auth
		if (twoFactorAuthFormVisible) {
			if (requestPhone) {
				handleSetPhoneNumber(e);
			} else {
				handleAuthCodeSubmit();
			}
		} else if (onboardingRoute === ONBOARDING_ROUTES.PRODUCER_ENTER_EMAIL) {
			dispatch(SessionActions.setEmailEntered(email));
			dispatch(SessionActions.setOnboardingRoute(ONBOARDING_ROUTES.PRODUCER_SELECT_APP));
		} else if (email && password) {
			// If Events and producer has verified 2FA in last
			// 30 days we pass this to events-api so they don't
			// have to verify device again
			const storedTwoFactorEvents = getStorageItem(StoredTokens.storedTwoFactorEvents);

			dispatch(SessionActions.signInHost(email, password, selectedApp, storedTwoFactorEvents));
		}
	}

	function checkEmail(e: React.ChangeEvent<HTMLInputElement>) {
		setEmail(e.target.value);
		setEmailValid(isValidEmail(e.target.value));
	}

	function checkPassword(e: React.ChangeEvent<HTMLInputElement>) {
		setPassword(e.target.value);
	}

	function forgotPassword() {
		if (selectedApp === APP.EVENTS
			|| selectedApp === APP.SHOWROOMS
			|| selectedApp === APP.ALL_HANDS
		) {
			let app;
			switch (selectedApp) {
				case APP.EVENTS:
					app = 'admin.brandlive.com';
					break;
				case APP.SHOWROOMS:
					app = 'the Showrooms app';
					break;
				case APP.ALL_HANDS:
					app = 'the Allhands app';
					break;
				default:
					break;
			}
			dispatch(SessionActions.setSignInError(`Please navigate to ${app} to reset your password`));
		} else {
			// We can do the forgot password logic for Events Legacy + SGR inside GR
			dispatch(SessionActions.setOnboardingRoute(ONBOARDING_ROUTES.PRODUCER_FORGOT_PASSWORD));
		}
	}

	function backButtonClicked() {
		if (isEventsProducer && !isEventsLegacyProducer && !isStandaloneProducer) {
			dispatch(SessionActions.clearProducerApps());
			dispatch(SessionActions.setOnboardingRoute(ONBOARDING_ROUTES.PRODUCER_ENTER_EMAIL));
		} else if (!isEventsProducer && isEventsLegacyProducer && !isStandaloneProducer) {
			dispatch(SessionActions.clearProducerApps());
			dispatch(SessionActions.setOnboardingRoute(ONBOARDING_ROUTES.PRODUCER_ENTER_EMAIL));
		} else if (!isEventsProducer && !isEventsLegacyProducer && isStandaloneProducer) {
			dispatch(SessionActions.clearProducerApps());
			dispatch(SessionActions.setOnboardingRoute(ONBOARDING_ROUTES.PRODUCER_ENTER_EMAIL));
		} else {
			dispatch(SessionActions.setOnboardingRoute(ONBOARDING_ROUTES.PRODUCER_SELECT_APP));
		}
	}

	console.log('signInError => ', signInError);

	return (
		<div className="panel producer sign-in">
			{verifying ? (
				<h2>Verifying your account...</h2>
			) : (
				<form onSubmit={submitForm}>
					{twoFactorAuthFormVisible && !twoFactorStored ? (
						requestPhone
							? <>
								<div className="presenter-message-container">
									<h2>Phone Number to Verify</h2>
								</div>
								<TextInput
									onChange={(e) => setPhoneNumber(e.target.value)}
									value={phoneNumber}
									placeholder="Enter your phone number"
									type="number"
								/>
								<button className="onboarding producer">Next</button>
							</>
							: <>
								<div className="presenter-message-container">
									<h2>Confirmation code sent to number ending in {phoneHash}</h2>
								</div>
								<TextInput
									onChange={(e) => setCode(e.target.value)}
									value={code}
									placeholder="Enter confirmation code"
									type="number"
								/>
								{signInError && (
									<div className="signin-error">
										<p>{typeof signInError !== 'string' ? signInError?.message : signInError}</p>
									</div>
								)}
								<button className="onboarding producer">Submit Code</button>
							</>
					) : (
						<>
							<div className="producer-logo-title">
								<img src={TellyChannelLogo} style={{ width: '48px', marginBottom: '15px' }} alt={'Connected'} />
								<div className="title-container">
									<span className="title green">Greenroom	<span className="title">Producer</span></span>
								</div>
							</div>

							<div className="email-input">
								{onboardingRoute === ONBOARDING_ROUTES.PRODUCER_ENTER_EMAIL
									&& <TextInput
										onChange={checkEmail}
										placeholder={'Email'}
										value={email}
										type={'email'}
										required
									/>
								}

								{onboardingRoute === ONBOARDING_ROUTES.PRODUCER_LOGIN && <>
									{selectedApp === APP.EVENTS
										&& <TextInput
											onChange={checkEmail}
											placeholder={'Brandlive Email'}
											value={email}
											type={'email'}
											required
										/>
									}

									{selectedApp === APP.GREENROOM
										&& <TextInput
											onChange={checkEmail}
											placeholder={'Greenroom Email'}
											value={email}
											type={'email'}
											required
										/>
									}

									<TextInput
										onChange={checkPassword}
										placeholder={'Password'}
										value={password}
										type={'password'}
										required
									/>

									<div className="forgot-password">
										<span><span onClick={forgotPassword} className="clickable">Forgot password?</span></span>
									</div>
								</>}
							</div>

							{invitationError && (
								<div className="signin-error">
									{/* -v-included because idk what breaking this <p> into 2 lines would do */}
									{/* eslint-disable-next-line max-len */}
									<p>It appears that this invitation does not match your account. Please sign in with a different account or check your invitation link and try again.</p>
								</div>
							)}
							{signInError && (
								<div className="signin-error">
									<p>{typeof signInError !== 'string' ? signInError?.message : signInError}</p>
								</div>
							)}
							<button className="onboarding producer" disabled={!emailValid}>Next</button>
							{onboardingRoute === ONBOARDING_ROUTES.PRODUCER_LOGIN
								&& <div className="back-button">
									<span>
										<span onClick={backButtonClicked} className="clickable">
											{`Back to ${isSingleAppProducer ? 'enter email' : 'select app'}`}
										</span>
									</span>
								</div>
							}
						</>
					)}
				</form>
			)}
		</div>
	);
}
